import React, { useId, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  Popover,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import type {
  RosteringDemandBlock,
  RosteringProposalAssignment,
  RosteringUncoveredDemand,
} from '../../features/rulesEngine/types';

// ─── types ────────────────────────────────────────────────────────────────────

export interface RosterTimelineProps {
  assignments: RosteringProposalAssignment[];
  demands: RosteringDemandBlock[];
  uncovered: RosteringUncoveredDemand[];
  /** driver UUID → display name */
  driverNameById: Record<string, string>;
  /** route_id → hex colour (from FixedRoute.color) */
  routeColorById: Record<string, string>;
}

type LabelMode = 'route' | 'driver';

// ─── helpers ──────────────────────────────────────────────────────────────────

const ROW_HEIGHT = 52;
const HEADER_HEIGHT = 32;
const LABEL_WIDTH = 120;
const MIN_BLOCK_PX = 36;

function toMs(iso: string): number {
  return new Date(iso).getTime();
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function fmtDuration(startIso: string, endIso: string): string {
  const mins = Math.round((toMs(endIso) - toMs(startIso)) / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m > 0 ? `${m}m` : ''}`.trim() : `${m}m`;
}

/** Lightens a hex colour for use as a block background. */
function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  const full = clean.length === 3
    ? clean.split('').map((c) => c + c).join('')
    : clean;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

const FALLBACK_ROUTE_COLOR = '#607d8b';
const UNCOVERED_COLOR = '#9e9e9e';

// ─── sub-components ───────────────────────────────────────────────────────────

interface BlockPopoverContent {
  routeName: string;
  driverName: string;
  vehicleId: string;
  startIso: string;
  endIso: string;
  tags: string[];
  status: 'pass' | 'warn' | 'fail' | 'uncovered';
  ruleMessages: string[];
}

function StatusChip({ status }: { status: BlockPopoverContent['status'] }) {
  const colorMap = {
    pass: 'success',
    warn: 'warning',
    fail: 'error',
    uncovered: 'default',
  } as const;
  return <Chip size="small" color={colorMap[status]} label={status} />;
}

// ─── main component ───────────────────────────────────────────────────────────

/**
 * Gantt-style roster timeline.
 * Y-axis rows = vehicles. X-axis = time (clipped to assignment span).
 * Block colour = route colour. Row tint = worst validation status on that vehicle.
 */
export default function RosterTimeline({
  assignments,
  demands,
  uncovered,
  driverNameById,
  routeColorById,
}: RosterTimelineProps) {
  const theme = useTheme();
  const titleId = useId();
  const [labelMode, setLabelMode] = useState<LabelMode>('route');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverContent, setPopoverContent] = useState<BlockPopoverContent | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // ── time span ──────────────────────────────────────────────────────────────

  const allTimes: number[] = [];
  assignments.forEach((a) => {
    allTimes.push(toMs(a.start_at), toMs(a.end_at));
  });
  uncovered.forEach((u) => {
    const d = demands.find((dem) => dem.demand_id === u.demand_id);
    if (d) allTimes.push(toMs(d.start_at), toMs(d.end_at));
  });

  if (!allTimes.length) return null;

  // Snap to whole hours for clean gridlines
  const spanStartMs = Math.floor(Math.min(...allTimes) / 3600000) * 3600000;
  const spanEndMs = Math.ceil(Math.max(...allTimes) / 3600000) * 3600000;
  const totalMs = spanEndMs - spanStartMs;

  function pct(ms: number): number {
    return ((ms - spanStartMs) / totalMs) * 100;
  }

  // ── vehicle rows ───────────────────────────────────────────────────────────

  const vehicleIds: string[] = [];
  assignments.forEach((a) => {
    const vid = a.vehicle_id ?? '__unassigned__';
    if (!vehicleIds.includes(vid)) vehicleIds.push(vid);
  });
  // Always show an unassigned row if there are uncovered demands
  if (uncovered.length && !vehicleIds.includes('__unassigned__')) {
    vehicleIds.push('__unassigned__');
  }

  // ── worst status per vehicle (for row tint) ────────────────────────────────

  const statusRank = { fail: 2, warn: 1, pass: 0 } as const;
  const vehicleWorstStatus: Record<string, 'pass' | 'warn' | 'fail'> = {};
  assignments.forEach((a) => {
    const vid = a.vehicle_id ?? '__unassigned__';
    const s = a.validation.overallStatus as 'pass' | 'warn' | 'fail';
    const current = vehicleWorstStatus[vid];
    if (!current || statusRank[s] > statusRank[current]) {
      vehicleWorstStatus[vid] = s;
    }
  });

  const rowTintColor: Record<string, string> = {
    pass: hexToRgba(theme.palette.success.light, 0.12),
    warn: hexToRgba(theme.palette.warning.light, 0.18),
    fail: hexToRgba(theme.palette.error.light, 0.18),
  };

  // ── hour gridlines ─────────────────────────────────────────────────────────

  const hourTicks: number[] = [];
  for (let ms = spanStartMs; ms <= spanEndMs; ms += 3600000) hourTicks.push(ms);

  // ── block click ───────────────────────────────────────────────────────────

  function openPopover(el: HTMLElement, content: BlockPopoverContent) {
    setAnchorEl(el);
    setPopoverContent(content);
  }

  function closePopover() {
    setAnchorEl(null);
    setPopoverContent(null);
  }

  // ── render ─────────────────────────────────────────────────────────────────

  const gridColor = theme.palette.divider;

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
        spacing={1}
      >
        <Typography variant="body2" color="text.secondary" id={titleId}>
          Vehicle timeline
        </Typography>
        <ToggleButtonGroup
          value={labelMode}
          exclusive
          size="small"
          onChange={(_, v: LabelMode | null) => { if (v) setLabelMode(v); }}
          aria-label="Block label mode"
        >
          <ToggleButton value="route" aria-label="Show route name">Route</ToggleButton>
          <ToggleButton value="driver" aria-label="Show driver name">Driver</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Timeline container */}
      <Box
        ref={containerRef}
        sx={{
          overflowX: 'auto',
          overflowY: 'visible',
          userSelect: 'none',
          border: `1px solid ${gridColor}`,
          borderRadius: 1.5,
          bgcolor: theme.palette.background.default,
        }}
        role="region"
        aria-labelledby={titleId}
      >
        <Box sx={{ minWidth: 600, p: 1 }}>

          {/* Hour header */}
          <Box sx={{ display: 'flex', ml: `${LABEL_WIDTH}px`, height: HEADER_HEIGHT, position: 'relative', borderBottom: `1px solid ${gridColor}` }}>
            {hourTicks.map((ms) => (
              <Box
                key={ms}
                sx={{
                  position: 'absolute',
                  left: `${pct(ms)}%`,
                  transform: 'translateX(-50%)',
                  whiteSpace: 'nowrap',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Vehicle rows */}
          {vehicleIds.map((vid) => {
            const isUnassigned = vid === '__unassigned__';
            const worst = vehicleWorstStatus[vid];
            const rowBg = isUnassigned
              ? hexToRgba(theme.palette.warning.light, 0.10)
              : worst ? rowTintColor[worst] : 'transparent';

            const rowAssignments = assignments.filter(
              (a) => (a.vehicle_id ?? '__unassigned__') === vid
            );
            const rowUncovered = isUnassigned
              ? uncovered.map((u) => demands.find((d) => d.demand_id === u.demand_id)).filter(Boolean) as RosteringDemandBlock[]
              : [];

            return (
              <Box
                key={vid}
                sx={{
                  display: 'flex',
                  height: ROW_HEIGHT,
                  borderBottom: `1px solid ${gridColor}`,
                  backgroundColor: rowBg,
                  '&:hover': { backgroundColor: isUnassigned ? rowBg : hexToRgba(theme.palette.action.hover, 0.5) },
                }}
              >
                {/* Row label */}
                <Box
                  sx={{
                    width: LABEL_WIDTH,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    borderRight: `1px solid ${gridColor}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    noWrap
                    color={isUnassigned ? 'text.secondary' : 'text.primary'}
                    sx={{ fontFamily: isUnassigned ? 'inherit' : 'monospace' }}
                  >
                    {isUnassigned ? 'Unassigned' : vid}
                  </Typography>
                </Box>

                {/* Track area */}
                <Box sx={{ flex: 1, position: 'relative' }}>
                  {/* Hour gridlines */}
                  {hourTicks.map((ms) => (
                    <Box
                      key={ms}
                      sx={{
                        position: 'absolute',
                        left: `${pct(ms)}%`,
                        top: 0,
                        bottom: 0,
                        borderLeft: `1px dashed ${gridColor}`,
                        pointerEvents: 'none',
                      }}
                    />
                  ))}

                  {/* Assignment blocks */}
                  {rowAssignments.map((a) => {
                    const demand = demands.find((d) => d.demand_id === a.demand_id);
                    const routeColor = (demand?.route_id && routeColorById[demand.route_id]) || FALLBACK_ROUTE_COLOR;
                    const leftPct = pct(toMs(a.start_at));
                    const widthPct = pct(toMs(a.end_at)) - leftPct;
                    const label = labelMode === 'route'
                      ? (demand?.route_name || a.route_ref || '—')
                      : ((a.driver_id && driverNameById[a.driver_id]) || a.driver_id || '—');
                    const flagged = a.validation.results.filter((r) => r.status === 'fail');
                    const hasWarning = a.validation.overallStatus === 'warn' || a.validation.overallStatus === 'fail';

                    return (
                      <Tooltip
                        key={a.demand_id}
                        title={`${fmtTime(a.start_at)}–${fmtTime(a.end_at)} · ${fmtDuration(a.start_at, a.end_at)}`}
                        placement="top"
                        arrow
                      >
                        <Box
                          onClick={(e) =>
                            openPopover(e.currentTarget, {
                              routeName: demand?.route_name || a.route_ref || '—',
                              driverName: (a.driver_id && driverNameById[a.driver_id]) || a.driver_id || '—',
                              vehicleId: a.vehicle_id || '—',
                              startIso: a.start_at,
                              endIso: a.end_at,
                              tags: a.required_vehicle_tags,
                              status: a.validation.overallStatus as 'pass' | 'warn' | 'fail',
                              ruleMessages: flagged.map((r) => r.message),
                            })
                          }
                          sx={{
                            position: 'absolute',
                            left: `${leftPct}%`,
                            width: `max(${widthPct}%, ${MIN_BLOCK_PX}px)`,
                            top: '6px',
                            bottom: '6px',
                            backgroundColor: hexToRgba(routeColor, 0.85),
                            border: `2px solid ${routeColor}`,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            px: 0.75,
                            cursor: 'pointer',
                            overflow: 'hidden',
                            boxSizing: 'border-box',
                            transition: 'filter 0.15s',
                            '&:hover': { filter: 'brightness(1.15)' },
                          }}
                        >
                          {hasWarning && (
                            <Box
                              sx={{
                                position: 'absolute',
                                right: 4,
                                top: 3,
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#fff',
                                color: a.validation.overallStatus === 'fail' ? 'error.main' : 'warning.main',
                                boxShadow: 1,
                              }}
                              aria-label="Assignment warning"
                            >
                              <WarningAmberRoundedIcon sx={{ fontSize: 11 }} />
                            </Box>
                          )}
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            noWrap
                            sx={{ color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                          >
                            {label}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}

                  {/* Uncovered demand blocks */}
                  {rowUncovered.map((d) => {
                    const leftPct = pct(toMs(d.start_at));
                    const widthPct = pct(toMs(d.end_at)) - leftPct;
                    const uncoveredReason = uncovered.find((u) => u.demand_id === d.demand_id)?.reason ?? '';
                    const humanReason = uncoveredReason.includes('Vehicle free')
                      ? uncoveredReason
                      : uncoveredReason.includes('hard constraints')
                        ? 'No driver/vehicle could be assigned without breaking a rule.'
                        : uncoveredReason || 'No feasible assignment found.';

                    return (
                      <Tooltip
                        key={d.demand_id}
                        title={`${d.route_name} · ${fmtTime(d.start_at)}–${fmtTime(d.end_at)} · Uncovered`}
                        placement="top"
                        arrow
                      >
                        <Box
                          onClick={(e) =>
                            openPopover(e.currentTarget, {
                              routeName: d.route_name || d.route_id,
                              driverName: '—',
                              vehicleId: '—',
                              startIso: d.start_at,
                              endIso: d.end_at,
                              tags: d.required_vehicle_tags,
                              status: 'uncovered',
                              ruleMessages: [humanReason],
                            })
                          }
                          sx={{
                            position: 'absolute',
                            left: `${leftPct}%`,
                            width: `max(${widthPct}%, ${MIN_BLOCK_PX}px)`,
                            top: '6px',
                            bottom: '6px',
                            backgroundColor: hexToRgba(UNCOVERED_COLOR, 0.25),
                            border: `2px dashed ${UNCOVERED_COLOR}`,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            px: 0.75,
                            cursor: 'pointer',
                            overflow: 'hidden',
                            boxSizing: 'border-box',
                            transition: 'filter 0.15s',
                            '&:hover': { filter: 'brightness(0.9)' },
                          }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            noWrap
                            color="text.secondary"
                          >
                            {d.route_name || d.route_id}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Detail popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { p: 2, minWidth: 240, maxWidth: 320 } } }}
      >
        {popoverContent && (
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" fontWeight={700}>
                {popoverContent.routeName}
              </Typography>
              <StatusChip status={popoverContent.status} />
            </Stack>
            <Divider />
            <Box>
              <Typography variant="caption" color="text.secondary">Time</Typography>
              <Typography variant="body2">
                {fmtTime(popoverContent.startIso)}–{fmtTime(popoverContent.endIso)}&nbsp;
                <Typography component="span" variant="caption" color="text.secondary">
                  ({fmtDuration(popoverContent.startIso, popoverContent.endIso)})
                </Typography>
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Driver</Typography>
              <Typography variant="body2">{popoverContent.driverName}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Vehicle</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{popoverContent.vehicleId}</Typography>
            </Box>
            {popoverContent.tags.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">Required tags</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.25 }}>
                  {popoverContent.tags.map((t) => (
                    <Chip key={t} label={t} size="small" variant="outlined" />
                  ))}
                </Stack>
              </Box>
            )}
            {popoverContent.ruleMessages.length > 0 && popoverContent.status !== 'pass' && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {popoverContent.status === 'uncovered' ? 'Reason' : 'Rule violations'}
                </Typography>
                {popoverContent.ruleMessages.map((m, i) => (
                  <Typography key={i} variant="body2" color="warning.dark" sx={{ mt: 0.25 }}>
                    • {m}
                  </Typography>
                ))}
              </Box>
            )}
          </Stack>
        )}
      </Popover>
    </Box>
  );
}
